/**
 * Merges two maps with array values into a single map. If a key exists in both maps,
 * the arrays for that key are concatenated.
 *
 * @template K - Type of the keys in the maps.
 * @template T - Type of the elements in the arrays.
 *
 * @param {Map<K, T[]>} map1 - The first map to merge.
 * @param {Map<K, T[]>} map2 - The second map to merge.
 * @returns {Map<K, T[]>} - The merged map containing keys from both input maps, with arrays concatenated for matching keys.
 * @example
 * // Example usage:
 * const map1 = new Map([['a', [1, 2]], ['b', [3]]]);
 * const map2 = new Map([['a', [4]], ['c', [5, 6]]]);
 * const merged = mergeMaps(map1, map2);
 * console.log(merged);
 * // Output: Map { 'a' => [ 1, 2, 4 ], 'b' => [ 3 ], 'c' => [ 5, 6 ] }
 */
export const mergeMaps = <K, T extends unknown[]>(map1: Map<K, T>, map2: Map<K, T>): Map<K, T> => {
	const mergedMap = new Map<K, T>();

	const addToMap = (map: Map<K, T>) => {
		map.forEach((value, key) => {
			if (mergedMap.has(key)) {
				const valuesWithoutDuplicates = value.filter((v) => !mergedMap.get(key).includes(v));
				mergedMap.set(key, [...mergedMap.get(key), ...valuesWithoutDuplicates] as T);
			} else {
				mergedMap.set(key, value);
			}
		});
	};

	addToMap(map1);
	addToMap(map2);

	return mergedMap;
};
